/* import __COLOCATED_TEMPLATE__ from './metric-card.hbs'; */
/* RESPONSIBLE TEAM: team-ai-insights */
import Component from '@glimmer/component';
import type { ReportState } from 'embercom/components/reporting/custom/report/chart-card';
import {
  staticDataResource,
  buildRenderableChart,
} from 'embercom/lib/reporting/static-data-resource';
import { getOwner } from '@ember/application';
import type Owner from '@ember/owner';
import { use } from 'ember-resources/util/function-resource';
import { inject as service } from '@ember/service';
import type IntlService from 'ember-intl/services/intl';
import type { PercentageMetricValue } from 'embercom/lib/reporting/chart-data-resource';
import { tracked } from '@glimmer/tracking';
import type ReportingUnderlyingDataService from 'embercom/services/reporting-underlying-data-service';
import type ChartSeries from 'embercom/models/reporting/custom/chart-series';
import type Range from 'embercom/models/reporting/range';
import { action } from '@ember/object';
import type AppService from 'embercom/services/app-service';
import type { Segment } from 'embercom/components/reporting/bespoke/fin-performance/bar-with-percentage';
import type ReportingMetricsService from 'embercom/services/reporting-metrics';
import type { PercentageMetric } from 'embercom/objects/reporting/unified/metrics/types';
import { isPresent } from '@ember/utils';
import type { Filter } from 'embercom/components/reporting/custom/filters';

interface Signature {
  Args: {
    title: string;
    ctaLabel?: string;
    ctaOnClick?: () => void;
    ctaTooltip?: string;
    descriptionKey: string;
    metricId: string;
    objects: any;
    chartDefinition: {
      visualization_type: string;
      chart_series: { metric_id: string }[];
    };
    reportState: ReportState;
    showTopLineMetric: boolean;
    isHeadlineMetric: boolean;
    headlineChartDefinition?: {
      visualization_type: string;
      chart_series: { metric_id: string }[];
    };
    hasSeparateCounterMetric?: boolean;
    attributeId?: string;
    chartTooltip: string;
    chartId?: string;
    isPreview?: boolean;
    showDescriptionInPreview?: boolean;
    showBannerOnlyWithData?: boolean;
  };
  Blocks: {
    banner?: [];
  };
}

const CX_OBJECT_KEY_TO_FILTER_VALUE: Record<string, number | string> = {
  key_5: 5,
  key_4: 4,
  key_3: 3,
  key_2: 2,
  key_1: 1,
  'Positive product feedback': 'product_feedback:positive',
  'High Fin answer quality': 'answer_quality_bot:high',
  'Positive user emotion': 'user_strong_emotion:positive',
  'Positive policy feedback': 'policy_feedback:positive',
  'Low user effort': 'user_effort:low',
  'High teammate answer quality': 'answer_quality_teammate:high',
  'Negative product feedback': 'product_feedback:negative',
  'Low Fin answer quality': 'answer_quality_bot:low',
  'Negative user emotion': 'user_strong_emotion:negative',
  'Negative policy feedback': 'policy_feedback:negative',
  'High user effort': 'user_effort:high',
  'Low teammate answer quality': 'answer_quality_teammate:low',
};

export default class MetricCard extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare appService: AppService;
  @service declare permissionsService: any;
  @service declare reportingUnderlyingDataService: ReportingUnderlyingDataService;
  @service declare reportingMetricsService: ReportingMetricsService;
  @service declare intercomEventService: any;
  @tracked selection: string[] = [];
  @tracked previousSelection: string[] = [];
  @tracked showUnderlyingData = false;
  @tracked drillInCustomDateRange: Range | undefined;
  @tracked chartSeries: ChartSeries | undefined;
  @tracked headerTitle: string | undefined;
  @use dataResource = staticDataResource(this, {
    serializedChart: () => this.args.chartDefinition,
    reportState: () => this.args.reportState,
  });

  @use headlineDataResource = staticDataResource(this, {
    serializedChart: () => this.headlineChartDefinition,
    reportState: () => this.args.reportState,
  });

  get renderableChart() {
    return this.dataResource.renderableChart;
  }

  get knownValuesEndpointSources() {
    let metricIds = this.args.chartDefinition.chart_series.map(
      (series: { metric_id: string }) => series.metric_id,
    );
    return metricIds.map(
      (metricId: string) => this.reportingMetricsService.getMetricById(metricId).firstSource,
    );
  }

  get isStandalone() {
    return this.appService.app.isStandaloneApp;
  }

  get columns() {
    return this.selection;
  }

  get canAccessDrillIn() {
    return this.permissionsService.currentAdminCan('can_reporting__drillin__access');
  }

  private defaultColumns(chartSeries: ChartSeries) {
    return this.reportingUnderlyingDataService.getDefaultColumns(chartSeries, this.renderableChart);
  }

  @action
  setDefaultColumns(chartSeries: ChartSeries) {
    this.selection = this.defaultColumns(chartSeries).uniq().compact();
    this.previousSelection = this.selection;
  }

  @action
  setColumns(chartSeries: ChartSeries, columns: string[]) {
    this.selection = this.reportingUnderlyingDataService.getOrderedAttributeIds(
      chartSeries.metric,
      columns,
      this.previousSelection,
    );

    this.previousSelection = this.selection;
  }

  private chartSeriesForSegment(segment: Segment) {
    let baseChartSeries = this.dataResource.renderableChart.chartSeries.firstObject as ChartSeries;
    if (segment.key === 'numerator') {
      return baseChartSeries;
    } else if (segment.key === 'denominator') {
      let denominatorMetricId = (baseChartSeries.metric as PercentageMetric).denominatorMetricId;
      let owner = getOwner(this) as Owner | undefined;
      if (!owner) {
        return undefined;
      }

      let renderableChart = buildRenderableChart(
        owner,
        {
          chart_series: [{ metric_id: denominatorMetricId }],
        },
        this.args.reportState,
      );

      if (isPresent(renderableChart?.chartSeries?.firstObject)) {
        return renderableChart.chartSeries.firstObject as ChartSeries;
      } else {
        return undefined;
      }
    } else {
      let filterValue = CX_OBJECT_KEY_TO_FILTER_VALUE[segment.key!];
      let owner = getOwner(this) as Owner | undefined;
      if (!owner) {
        return undefined;
      }

      // Get existing filters from base chart series
      let existingFilters = baseChartSeries.filters || { type: 'and', filters: [] };

      // Create the new segment filter
      let segmentFilter = {
        type: 'category',
        data: {
          property: this.args.attributeId,
          values: [filterValue],
        },
      };

      // Keep existing filters but remove any filter with the same property
      let filteredExistingFilters = existingFilters.filters.filter(
        (f: Filter) => f.data?.property !== this.args.attributeId,
      );

      // Merge: existing filters (minus the one we're replacing) + new segment filter
      let mergedFilters = {
        type: 'and' as const,
        filters: [...filteredExistingFilters, segmentFilter],
      };

      let renderableChart = buildRenderableChart(
        owner,
        {
          chart_series: [
            {
              metric_id: baseChartSeries.metric.id,
              filters: mergedFilters,
            },
          ],
        },
        this.args.reportState,
      );

      if (isPresent(renderableChart?.chartSeries?.firstObject)) {
        return renderableChart.chartSeries.firstObject as ChartSeries;
      } else {
        return undefined;
      }
    }
  }

  @action
  openUnderlyingDataModal(segment: Segment) {
    if (this.canAccessDrillIn) {
      let chartSeriesForSegment = this.chartSeriesForSegment(segment);
      if (chartSeriesForSegment) {
        this.intercomEventService.trackAnalyticsEvent({
          action: 'opened',
          object: 'underlying_data_modal',
          chart_metric_id: chartSeriesForSegment.metric.id,
          chart_id: this.args.chartId,
        });

        this.drillInCustomDateRange = this.args.reportState.dateRange;
        this.setDefaultColumns(chartSeriesForSegment);
        this.chartSeries = chartSeriesForSegment;
        this.showUnderlyingData = true;
        this.headerTitle = segment.headerTitle || undefined;
      }
    } else {
      this.permissionsService.loadAllAdminsAndShowPermissionRequestModal(
        'can_reporting__drillin__access',
      );
    }
  }

  @action
  onCloseUnderlyingDataModal() {
    this.showUnderlyingData = false;
  }

  get headlineChartDefinition() {
    return (
      this.args.headlineChartDefinition ?? {
        visualization_type: 'counter',
        chart_series: [{ metric_id: 'v1.new_conversations' }],
      }
    );
  }

  get hasData() {
    if (this.dataResource.renderableChart.viewBy) {
      return this.dataResource.hasData;
    } else {
      return (
        this.dataResource.kpiData &&
        (this.dataResource.kpiData[0] as PercentageMetricValue).value !== undefined
      );
    }
  }

  get numeratorValue() {
    return this.args.hasSeparateCounterMetric
      ? (this.headlineDataResource.kpiData[0] as PercentageMetricValue).numeratorValue
      : (this.dataResource.kpiData[0] as PercentageMetricValue).numeratorValue;
  }

  get numerator() {
    return this.dataResource.formatValue({
      value: this.numeratorValue,
      metricId: 'v1.new_conversations',
    });
  }

  get denominatorValue() {
    return this.args.hasSeparateCounterMetric
      ? (this.headlineDataResource.kpiData[0] as PercentageMetricValue).denominatorValue
      : (this.dataResource.kpiData[0] as PercentageMetricValue).denominatorValue;
  }

  get denominator() {
    let denominatorValue = this.denominatorValue;
    return this.dataResource.formatValue({
      value: denominatorValue,
      metricId: 'v1.new_conversations',
    });
  }

  get isLoading() {
    return this.dataResource.isLoading || this.headlineDataResource.isLoading;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::Bespoke::FinPerformance::MetricCard': typeof MetricCard;
    'reporting/bespoke/fin-performance/metric-card': typeof MetricCard;
  }
}
